import { useQuery } from "react-query";
import { AUDIT } from "../../config/apiConfig";
import { Badge, Modal, Spinner, Table } from "flowbite-react";
import axios from "../../config/axiosInstance";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatToReal } from "../../utils/utils";

const ActionBadge = ({ action }) => {
  const m = {
    CREATE: { color: "success", label: "Criado" },
    UPDATE: { color: "info", label: "Atualizado" },
    DELETE: { color: "failure", label: "Removido" },
  }[action] || { color: "gray", label: action };
  return <Badge color={m.color}>{m.label}</Badge>;
};

// Heurística simples para campos monetários
const isMoneyField = (field) =>
  /price|valor|amount|custo|tarifa|margem|margin|fee|preco/i.test(field);

// Renderiza valor com ou sem R$
const renderVal = (field, v) => {
  if (v == null || v === "") return "—";
  if (isMoneyField(field)) return formatToReal(v);
  return String(v);
};

const AuditHistoryModal = ({ open, onClose, cqpId, title, companyId }) => {
  const { data, isFetching, isError } = useQuery(
    ["audit-cqp", cqpId, companyId], // <-- inclui companyId na query key
    async () => {
      const url = AUDIT.LIST_BY_ENTITY("CompanyQueryPermission", cqpId);
      const { data } = await axios.get(url, {
        params: {
          page: 1,
          pageSize: 50,
          // IMPORTANTÍSSIMO: filtra por empresa para não cruzar históricos
          companyId: companyId ?? undefined,
        },
      });
      return data;
    },
    { enabled: open && !!cqpId }
  );

  const rows = data?.data || [];

  return (
    <Modal show={open} onClose={onClose} size="5xl">
      <Modal.Header>
        Histórico — {title || "Permissão de consulta"}
      </Modal.Header>
      <Modal.Body>
        {isFetching ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" />
          </div>
        ) : isError ? (
          <div className="text-center text-red-600">
            Erro ao carregar histórico.
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center text-gray-500">
            Nenhum evento encontrado para esta permissão.
          </div>
        ) : (
          <Table>
            <Table.Head>
              <Table.HeadCell>Quando</Table.HeadCell>
              <Table.HeadCell>Ação</Table.HeadCell>
              <Table.HeadCell>Usuário</Table.HeadCell>
              <Table.HeadCell>Campos</Table.HeadCell>
              <Table.HeadCell>Antes → Depois</Table.HeadCell>
              <Table.HeadCell>Motivo</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y text-xs">
              {rows.map((r) => {
                const when = r.createdAt
                  ? format(new Date(r.createdAt), "dd/MM/yy HH:mm", {
                      locale: ptBR,
                    })
                  : "-";
                const who = r.changedBy?.name || r.changedByUserId || "—";
                const fields = Array.isArray(r.changedFields)
                  ? r.changedFields
                  : [];
                return (
                  <Table.Row key={r.id}>
                    <Table.Cell>{when}</Table.Cell>
                    <Table.Cell>
                      <ActionBadge action={r.action} />
                    </Table.Cell>
                    <Table.Cell>{who}</Table.Cell>
                    <Table.Cell>
                      {fields.length ? (
                        <div className="flex flex-wrap gap-1">
                          {fields.map((f) => (
                            <Badge key={f} color="gray" size="xs">
                              {f}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="text-xs text-gray-700 dark:text-gray-300">
                        {fields.length
                          ? fields.map((f) => (
                              <div key={f} className="mb-1">
                                <span className="font-medium">{f}: </span>
                                <span className="line-through opacity-70 mr-1">
                                  {renderVal(f, r?.previousValues?.[f])}
                                </span>
                                <span>
                                  {" "}
                                  → {renderVal(f, r?.newValues?.[f])}
                                </span>
                              </div>
                            ))
                          : "—"}
                      </div>
                    </Table.Cell>
                    <Table.Cell
                      className="max-w-[240px] truncate"
                      title={r.reason || ""}
                    >
                      {r.reason || "—"}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AuditHistoryModal;
